import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateCompany } from "../hooks/useCompanies";
import { useCategoryItems } from "@/modules/dropdowns/hooks/useDropdowns";
import type { CompanyCategory } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Locked discriminator passed by the tab/page that opened the drawer.
   * The Type dropdown only shows values whose metadata.category matches.
   * Defaults to OPERATIONAL when omitted (e.g. when opening from the
   * subsidiary action on a parent company detail page).
   */
  category?: CompanyCategory;
  parentId?: string;
  titleOverride?: string;
  onCreated?: (company: {
    id: string;
    name: string;
    code: string;
    types: string[];
    category: CompanyCategory;
  }) => void;
};

export function NewCompanyDrawer({
  isOpen,
  onClose,
  category,
  parentId,
  titleOverride,
  onCreated,
}: Props) {
  const createCompany = useCreateCompany();

  const effectiveCategory: CompanyCategory = category ?? "OPERATIONAL";

  // Fetch only the type values whose metadata.category matches this drawer's
  // intent — that way the user cannot pick a PARENT type from the External tab
  // and the API's metadata-mismatch guard is never reached in normal use.
  const { data: typeOptions = [], isLoading: typesLoading } = useCategoryItems(
    "company_type",
    undefined,
    { category: effectiveCategory },
  );

  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [types, setTypes] = useState<string[]>([]);
  const [status, setStatus] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
  const [industry, setIndustry] = useState("");
  const [currency, setCurrency] = useState("AED");

  // Re-seed form on each open (or when the category/parentId props change)
  // so a tab switch can never leave a stale type from the prior context —
  // the original cause of the External-tab-creates-as-PARENT bug.
  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setShortName("");
    setStatus("ACTIVE");
    setIndustry("");
    setCurrency("AED");
    // types is set in the next effect, once typeOptions are loaded
  }, [isOpen, effectiveCategory, parentId]);

  // When type options arrive (or change because the category changed),
  // pick a sensible default. Prefer SUBSIDIARY when creating a child of an
  // operational parent; otherwise the first available active option.
  useEffect(() => {
    if (!isOpen) return;
    if (typeOptions.length === 0) {
      setTypes([]);
      return;
    }
    const active = typeOptions.filter((t) => t.isActive);
    if (active.length === 0) {
      setTypes([]);
      return;
    }
    const preferred =
      parentId && effectiveCategory === "OPERATIONAL"
        ? active.find((t) => t.value === "SUBSIDIARY")
        : undefined;
    setTypes([(preferred ?? active[0]).value]);
  }, [isOpen, effectiveCategory, parentId, typeOptions]);

  const toggleType = (value: string) => {
    setTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || types.length === 0) return;
    const res = await createCompany.mutateAsync({
      name: name.trim(),
      shortName: shortName.trim() || undefined,
      types,
      category: effectiveCategory,
      status,
      industry: industry.trim() || undefined,
      currency,
      parentId,
    });
    onCreated?.({
      id: res.company.id,
      name: res.company.name,
      code: res.company.code,
      types: res.company.types,
      category: res.company.category,
    });
    onClose();
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            {titleOverride ?? "New Company"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Basic Information
            </h3>

            <div className="space-y-2">
              <Label htmlFor="company-name">
                Company Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="company-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter company name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-short-name">Short Name</Label>
              <Input
                id="company-short-name"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                placeholder="e.g. PX"
              />
            </div>

            <div className="space-y-2">
              <Label>
                Types <span className="text-destructive">*</span>
              </Label>
              <div
                className="grid gap-2 rounded-md border p-3"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              >
                {typesLoading && (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                )}
                {!typesLoading && typeOptions.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No types configured for this category.
                  </p>
                )}
                {typeOptions
                  .filter((t) => t.isActive)
                  .map((t) => (
                    <label
                      key={t.id}
                      className="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={types.includes(t.value)}
                        onCheckedChange={() => toggleType(t.value)}
                      />
                      <span className="text-sm font-medium">{t.label}</span>
                    </label>
                  ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Select all that apply. A company can hold multiple roles.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-status">Status</Label>
              <Select
                id="company-status"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as "ACTIVE" | "INACTIVE")
                }
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-industry">Industry</Label>
              <Input
                id="company-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Technology"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company-currency">Currency</Label>
              <Select
                id="company-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="AED">AED — UAE Dirham</option>
                <option value="USD">USD — US Dollar</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="EUR">EUR — Euro</option>
                <option value="INR">INR — Indian Rupee</option>
                <option value="SAR">SAR — Saudi Riyal</option>
                <option value="QAR">QAR — Qatari Riyal</option>
                <option value="KWD">KWD — Kuwaiti Dinar</option>
              </Select>
            </div>
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() || types.length === 0 || createCompany.isPending
              }
            >
              {createCompany.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create Company
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
